import Cookies from 'cookie-universal';
import { groupBy, remove } from 'lodash-es';
import { http, UrlUtils } from '@article/pixel';
import { gql } from '@apollo/client/core';
import {
  trackEvent, //
  getCartQuantity,
  checkLoggedIn,
  updateFavoriteHeader,
  executeQuery,
  browseRoomPageTestCondition,
} from '@/components/commons/utils.js';
import { getInitialState } from '@/components/commons/ServerSideRendererUtils.js';
import CountryUtils from '@/components/commons/CountryUtils.js';
import Chat from '@/components/commons/Chat.js';
// eslint-disable-next-line import/extensions
import { createRouter } from '@/router';

import UserService from '@/services/UserService';
import LocationService from '@/services/LocationService';
import SessionService from '@/services/SessionService';
import MessagingService from '@/services/MessagingService';
import Tracking from '@/components/commons/Tracking';
import useBreadcrumb from '@/compositions/useBreadcrumb.js';
import useFavorite from '@/compositions/useFavorite';

const router = createRouter();

const COMMON_DATA_QUERY = gql`
  query commonData {
    commonData {
      country {
        id
        code
      }
      currency {
        id
        code
      }
      categories {
        id
        name
        displayName
        url
        parent {
          id
        }
        path
        displayImage {
          url
          key
        }
        sort
      }
      primaryRooms {
        id
        value
        name
        displayName
        url
      }
      urls {
        static
        images
        getProducts
        getOrders
        browseReviews
        getOrderTracking
        subscription
        switchCurrency
        getProductReviews
      }
      promotion {
        id
        name
        caName
        startDate
        endDate
        isoEndDate
        saleMessage
        caSaleMessage
      }
      financing {
        affirmEnabled
        affirmMaxAmount
        affirmMinAmount
        affirmKey
        affirmUrl
        paybrightEnabled
        paybrightMaxAmount
        paybrightMinAmount
        paybrightKey
        paybrightUrl
        paybrightModalUrl
      }
      clearanceNavigationEnabled
      giftCardNavigationEnabled
      hasClearanceProductsCA
      hasClearanceProductsUS
    }
  }
`;

const LOAD_CART_QUERY = gql`
  query miniCart {
    storeMiniCartGet {
      success
      cartInfo {
        countryId
        countryCode
        currencyId
        currencySymbol
        cartItems {
          productId
          quantity
        }
        summary {
          subtotalWithSaleSavings
          items {
            productId
            productTitle
            productUrl
            quantity
            originalPrice
            productPackagedItemQuantity
            price
            productImage {
              key
              url
              alt
            }
            isOnSale
            isOnClearance
          }
        }
        orderInfos {
          subtotal
          total
          tax
          taxRate
          isTaxExempt
          shippingOptionId
          shippingCost
          checkoutGroup
          items {
            productId
            quantity
            productPackagedItemQuantity
            productSkuNo
            productTitle
            productUrl
            price
            priceCents
            originalPrice
            originalPriceCents
            originalAmount
            originalAmountCents
            amount
            amountCents
            isOnSale
            isOnClearance
          }
          orderDiscounts {
            code
            type
            percentage
            amount
            minimumPurchase
            discountAmount
            discountAmountCents
            currencySymbol
            isDynamicDeliveryCostDiscount
            quantity
          }
        }
        shippingOptionsSelected
        trade
        useCredits
        fullyPaidByStoreCredit
        hasAvailableCredit
        availableCredit
        availableCreditCents
        creditsToUse
        creditsToUseCents
        creditsToUse
        creditsToUseCents
        orderTotalLessCredits
        orderTotalLessCreditsCents
        totalWithTaxEstimate
        totalWithTaxEstimateCents
        tax
        total
        calculationDate
        splittable
        enablePayBright
        enablePaypal
        enableStripe
        enableResolvePay
        enableAffirm
      }
    }
  }
`;

export const actions = {
  async fetchData({ commit }) {
    try {
      const result = await executeQuery(COMMON_DATA_QUERY);
      commit('updateCommonData', JSON.parse(JSON.stringify(result.commonData)));
    } catch (error) {
      console.error('fetchData', error);
    }
  },

  async chat({ commit, state }) {
    const chatAfterHours = router.resolve({ name: 'message', query: { 'after-hours': true } }).href;
    Chat.start(chatAfterHours);
  },

  async search({ commit, state }, query) {
    window.location = router.resolve({ name: 'search', query: { q: query.trim() } }).href;
  },

  async miniCartUpdateQuantity({ state, commit }, { productId, quantity }) {
    try {
      const { success, cartItems } = await http.post('/api/cart/update', {
        items: [
          {
            productId,
            quantity,
          },
        ],
      });

      if (success && cartItems) {
        console.log(cartItems);
        return;
      }
    } catch (e) {
      trackCartError(SERVER_ERROR, e);
      throw e;
    }
  },

  async openMiniCart({ commit, state }) {
    const data = await loadCartData();
    if (data) {
      UserService.setCart(data.cartInfo);
    }
    commit('updateMiniCart', { cart: data.cartInfo, cartCount: getCartQuantity() });
    commit('updateCartFetched', true);
  },

  async checkout({ state }) {
    trackEvent('Checkout', 'Initiate Checkout');
    window.location = router.resolve({ name: 'cart' }).href;
  },

  async changeCountry(_, { country, shouldReload = true }) {
    LocationService.setCountry(CountryUtils[country] || CountryUtils.US, {
      shouldReload,
    });
  },

  async closeBanner({ state }) {
    if (state.shippingDiscount) {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      Cookies().set('article-shipping-discount', true, { expires: date });
    }
  },

  async updateLocation({ dispatch }, { zip, shouldReload }) {
    dispatch('loadMessaging', zip);
    return dispatch('getSessionData', { zip, shouldReload });
  },

  async loadMessaging({ commit }, zip) {
    const { common, messaging } = await MessagingService.fetchMessaging(zip);

    window.livechatOverlayEnabled = common.livechatOverlayEnabled;

    commit('siteMessage', messaging.message?.text);
    commit('etaMessage', messaging.message?.etaText);

    commit('updateShippingDiscount', {
      message: messaging.legacyShippingDiscountMessage,
      details: messaging.legacyShippingDiscountDetails,
      baselineShippingCostDiscounted: messaging.baselineShippingCostDiscounted,
      shippingDiscountThreshold: messaging.shippingDiscountThreshold,
      baselineShippingCost: messaging.baselineShippingCost,
    });

    commit('updateUgcPixleeEnabled', !!common.ugcPixleeEnabled);
    commit('updateBlackFridayContactEnabled', !!common.blackFridayContactEnabled);
    commit('updateCsSupportHours', {
      csPhoneWeekdaysHoursStart: common.csPhoneWeekdaysHoursStart,
      csPhoneWeekdaysHoursEnd: common.csPhoneWeekdaysHoursEnd,
      csPhoneWeekendHoursStart: common.csPhoneWeekendHoursStart,
      csPhoneWeekendHoursEnd: common.csPhoneWeekendHoursEnd,
      csChatWeekdaysHoursStart: common.csChatWeekdaysHoursStart,
      csChatWeekdaysHoursEnd: common.csChatWeekdaysHoursEnd,
      csChatWeekendHoursStart: common.csChatWeekendHoursStart,
      csChatWeekendHoursEnd: common.csChatWeekendHoursEnd,
      csChatOverlayWeekdaysHoursStart: common.csChatOverlayWeekdaysHoursStart,
      csChatOverlayWeekdaysHoursEnd: common.csChatOverlayWeekdaysHoursEnd,
      csChatOverlayWeekendHoursStart: common.csChatOverlayWeekendHoursStart,
      csChatOverlayWeekendHoursEnd: common.csChatOverlayWeekendHoursEnd,
    });
  },

  async refresh({ commit }) {
    commit('updateMiniCartCount', getCartQuantity());
    commit('updateMiniCart', { cart: UserService.getCart(), cartCount: getCartQuantity() });

    const { getFavorite } = useFavorite();
    getFavorite();

    checkLoggedIn();
    updateFavoriteHeader();
  },

  async notifyCartItemsAdded({ commit }) {
    commit('updateNotifyCartItemsAdded', true);
    const data = await loadCartData();
    if (data) {
      UserService.setCart(data.cartInfo);
    }
    commit('updateMiniCart', { cart: data.cartInfo, cartCount: getCartQuantity() });
    commit('updateCartFetched', true);
  },

  async loadProductsForFile({ commit }, id) {
    commit('updateActiveFileProducts', []);
    const filter = `filter[fileProduct]=file.id==${id};product.status!='DISCONTINUED'`;
    const include = `include=product`;
    const fields = `fields[product]=id,imageUrl,popularity,title,basePrice,salePrice,url,status,skuNo`;
    const response = await fetch(`${window.apiRoutes.krakenMediaAPI}/fileProduct?${filter}&${include}&${fields}`);
    const responseJson = await response.json();
    commit('updateActiveFileProducts', JSON.parse(JSON.stringify(responseJson)).included);
  },

  async getSessionData({ commit, dispatch }, payload) {
    const defaultPayload = {
      zip: '',
      shouldReload: true,
    };

    const { zip, shouldReload } = {
      ...defaultPayload,
      ...(payload || {}),
    };

    try {
      const pingPromise = process.env.STOREFRONT ? SessionService.pinggql(zip, UserService.getCustomerToken()) : SessionService.ping(zip);
      const {
        user, //
        customerToken,
        office,
        geoLocation,
        zipLocation,
        error,
      } = await pingPromise;

      if (error) {
        return {};
      }

      if (user?.expired) {
        UserService.setUser(null);
        commit('updateUser', null);
      } else {
        UserService.setUser(user);
        commit('updateUser', user);
      }

      if (customerToken != null) {
        UserService.setCustomerToken(customerToken);
        commit('updateCustomerToken', customerToken);
      }

      if (office != null) {
        LocationService.setOffice(office);
        commit('updateOffice', office);
      }

      if (!zip || (zip && zipLocation)) {
        LocationService.setLocation(
          {
            geoLocation,
            zipLocation,
          },
          shouldReload,
        );

        if (geoLocation != null) {
          commit('updateGeoLocation', geoLocation);
        }

        if (zipLocation != null) {
          commit('updateZipLocation', zipLocation);
        }
      }

      commit('updateInitializedSessionData', true);

      dispatch('refresh');

      return {
        user,
        office,
        geoLocation,
        zipLocation,
      };
    } catch (error) {
      Tracking.sentryCaptureError(error);
    }

    commit('updateInitializedSessionData', true);

    return {};
  },

  refreshUserData({ commit, dispatch }) {
    try {
      UserService.refreshUser();

      const user = UserService.getUser();

      if (user?.expired) {
        commit('updateUser', null);
      } else {
        commit('updateUser', user);
      }

      dispatch('refreshCartData');

      const { getFavorite } = useFavorite();
      getFavorite();
    } catch (error) {
      Tracking.sentryCaptureError(error);
    }
  },

  refreshCartData({ dispatch }) {
    try {
      UserService.refreshCart();

      dispatch('refresh');
    } catch (error) {
      Tracking.sentryCaptureError(error);
    }
  },

  getUser({ commit }) {
    if (UserService.getUser()) {
      commit('updateUser', UserService.getUser());
    }

    if (UserService.getSessionId()) {
      http.attach({
        __i: UserService.getSessionId(),
      });
    }
  },

  setUser({ commit }, payload) {
    UserService.setUser(payload);
    commit('updateUser', payload);
  },

  setCustomerToken({ commit }, payload) {
    UserService.setCustomerToken(payload);
    commit('updateCustomerToken', payload);
  },
};

const mutations = {
  updateCurrentRoom(state, payload) {
    state.currentRoom = payload;
  },
  updateActiveFileProducts(state, products) {
    state.activeFileProducts = products;
  },
  updateCommonData(state, common) {
    const {
      categories, //
      primaryRooms,
      urls,
      promotion,
      hasClearanceProductsCA,
      hasClearanceProductsUS,
      giftCardNavigationEnabled,
      financing,
    } = common;

    const hasClearance = LocationService.isCanada() ? hasClearanceProductsCA : hasClearanceProductsUS;

    categories.forEach((category) => {
      if (category.parent) {
        category.parentId = category.parent.id;
      }
    });
    state.menuItems = formatMenuItems(categories, primaryRooms, {
      // showPromotion: !!promotion,
      // showPromotion: promotion,
      promotion,
      showGiftCards: giftCardNavigationEnabled,
      showClearance: hasClearance,
    });
    state.clearanceNavigationEnabled = hasClearance;
    state.urls = urls;
    state.promotion = promotion;
    Object.assign(state.financing, financing);

    state.promoFinPage = common.promotion ? getFinPromoPage(common) : {}; // OK for now, financing page is not SSR
    state.currentPromo = common.promotion ? getCurrentPromo(common.promotion) : '';
  },
  updateIntialized(state, initialized) {
    state.initialized = initialized;
  },

  updateShippingDiscount(state, shippingDiscount) {
    state.shippingDiscount = shippingDiscount;

    if (shippingDiscount && shippingDiscount.message) {
      const customerClosedBanner = Cookies().get('article-shipping-discount', { parseJSON: false }) && !getContactBannerABTestCondition();
      if (!customerClosedBanner && !state.user?.isTrade) {
        state.bannerMessage = shippingDiscount.message;
      } else {
        state.bannerMessage = null;
      }
    } else {
      state.bannerMessage = null;
    }
  },

  siteMessage(state, message) {
    state.siteMessage = message;
  },

  etaMessage(state, message) {
    state.etaMessage = message;
  },

  updateMiniCart(state, { cart, cartCount }) {
    state.cart = cart;
    state.cartCount = cartCount;
  },

  updateMiniCartCount(state, cartCount) {
    state.cartCount = cartCount;
  },

  updateCurrency(state, currencyId) {
    state.currencyId = currencyId;
    state.countryId = CountryUtils.getCountryByCurrencyId(currencyId).countryId;
  },

  updateCountry(state, countryId) {
    state.countryId = countryId;
    state.currencyId = CountryUtils.getCountryById(countryId).currencyId;
  },

  updateNotifyCartItemsAdded(state, notify) {
    state.notifyCartItemsAdded = notify;
  },

  updateUgcPixleeEnabled(state, payload) {
    state.ugcPixleeEnabled = payload;
  },

  updateBlackFridayContactEnabled(state, payload) {
    state.blackFridayContactEnabled = payload;
  },

  updatePaybrightUrl(state, payload) {
    state.payBrightMoreInfoUrl = payload;
  },

  togglePaybrightModal(state) {
    state.showPaybrightModal = !state.showPaybrightModal;
  },

  toggleAffirmModal(state) {
    state.showAffirmModal = !state.showAffirmModal;
  },

  updateAffirmUrl(state, payload) {
    state.affirmModalUrl = payload;
  },

  updateCsSupportHours(state, payload) {
    if (payload && Object.keys(payload).length) {
      Object.entries(payload).forEach(([key, value]) => {
        state[key] = value;
      });
    }
  },

  updateUser(state, payload) {
    state.user = payload;
  },

  updateCustomerToken(state, payload) {
    state.customerToken = payload;
  },

  updateGeoLocation(state, payload) {
    state.geoLocation = payload;
  },

  updateZipLocation(state, payload) {
    state.zipLocation = payload;
  },

  updateOffice(state, payload) {
    state.isOffice = payload;
  },

  updateInitializedSessionData(state, payload) {
    state.initializedSessionData = payload;
  },

  updateCartFetched(state, payload) {
    state.cartFetched = payload;
  },

  updateCurrentRoute(state, payload) {
    state.currentRoute = payload;
  },
  updateRoomIdeasTracking(state, payload) {
    state.roomIdeasTracking = payload;
  },
};

const getters = {
  currentCountry(state) {
    const country = { ...CountryUtils.getCountryByCurrencyId(state.currencyId) };
    return Object.assign(country, { alt: `${country.name} Flag` });
  },

  // Match the current route to a menu item
  routeMenuItem(state) {
    const route = Object.assign({}, state.currentRoute);

    if (!getBrowseRoomPageTestCondition()) {
      const flatMenuItems = state.menuItems.map((item) => (item.items && item.items.length ? item.items : item)).flat();
      const matchingItems = flatMenuItems.filter((item) => {
        if (route.name === 'browse' && route.params && route.params.id) {
          return `${item.id}` === route.params.id;
        }
        return `${item.id}` === route.name;
      });
      return matchingItems.length ? matchingItems[0] : null;
    }

    const rooms = ['living room', 'dining room', 'bedroom', 'outdoor', 'office', 'entryway'];
    const sales = ['all sale', 'current sale', 'clearance sale'];
    let flatMenuItems = state.menuItems?.map((item) => (item.items && item.items.length ? item.items : item))?.flat() ?? [];

    flatMenuItems = flatMenuItems
      .map((item) => (item.items && item.items.length ? item.items : item))
      .flat()
      .map((flatMenuItem) => {
        const name = flatMenuItem.name.toLowerCase();
        if (sales.indexOf(name) >= 0 || name.includes('sale')) {
          flatMenuItem.parentMenuItem = 'sale';
        } else if (rooms.indexOf(name) >= 0) {
          flatMenuItem.parentMenuItem = 'room';
        } else {
          flatMenuItem.parentMenuItem = 'product';
        }
        return flatMenuItem;
      });

    const matchingItems = flatMenuItems.filter((item) => {
      if (route.name === 'browse' && route.params && route.params.id) {
        return `${item.id}` === route.params.id;
      }
      if (route.name === 'room' && route.params && route.params.id) {
        return `${item.id}` === route.params.id;
      }
      return `${item.id}` === route.name;
    });

    return matchingItems.length ? matchingItems[0] : null;
  },

  isRegisteredUser(state) {
    return state.user?.registered ?? false;
  },

  isVerifiedUser(state) {
    return state.user?.verified ?? false;
  },
  roomIdeasTracking(state) {
    return state.roomIdeasTracking;
  },
};

export default class CommonStore {
  static initialize() {
    const initialState = getInitialState();

    let state = {
      user: {
        cell: '',
        email: '',
        expired: false,
        firstName: '',
        isTrade: false,
        lastLoginDate: '',
        lastLoginIp: '',
        lastName: '',
        registerIp: '',
        registered: false,
        sessionId: '',
        sessionIp: '',
        verified: false,
        verifyDate: '',
        verifyIp: '',
      },
      geoLocation: {
        accuracyRadius: '',
        countryId: '',
        countryCode: '',
        regionId: '',
        regionCode: '',
        city: '',
        currencyId: '',
        zipcode: '',
      },
      zipLocation: {
        accuracyRadius: '',
        countryId: '',
        countryCode: '',
        regionId: '',
        regionCode: '',
        city: '',
        currencyId: '',
        zipcode: '',
      },
      isOffice: false,
      client: true,
      initialized: false,
      countryId: null,
      currencyId: null,
      urls: {},
      notifyCartItemsAdded: false,
      ugcPixleeEnabled: true,
      blackFridayContactEnabled: true,
      enableBrowseRoomPages: getBrowseRoomPageTestCondition(),
      shippingDiscount: {},
      activeFileProducts: [],
      currentRoom: '',
      siteMessage: null,
      etaMessage: null,
      bannerMessage: null,
      financing: {
        affirmEnabled: false,
        affirmMaxAmount: null,
        affirmMinAmount: null,
        affirmKey: null,
        affirmUrl: null,
        paybrightEnabled: false,
        paybrightMaxAmount: null,
        paybrightMinAmount: null,
        paybrightKey: null,
        paybrightUrl: null,
        paybrightModalUrl: null,
      },
      payment: {
        stripe: {
          usPublishableKey: null,
          canadaPublishableKey: null,
        },
        paypal: {
          clientId: null,
        },
      },
      csPhoneWeekdaysHoursStart: '',
      csPhoneWeekdaysHoursEnd: '',
      csPhoneWeekendHoursStart: '',
      csPhoneWeekendHoursEnd: '',
      csChatWeekdaysHoursStart: '',
      csChatWeekdaysHoursEnd: '',
      csChatWeekendHoursStart: '',
      csChatWeekendHoursEnd: '',
      csChatOverlayWeekdaysHoursStart: '',
      csChatOverlayWeekdaysHoursEnd: '',
      csChatOverlayWeekendHoursStart: '',
      csChatOverlayWeekendHoursEnd: '',
      initializedSessionData: false,
      cartFetched: false,
      primaryRooms: [],
      menuItems: [],
      showContactBanner: getContactBannerABTestCondition(),
      showMobileCategoryMenu: getMobileCategoryMenuTestCondition(),
      enableSearchSuggestions: getNewSearchABTestCondition(),
      cartCount: 0,
      cart: null,
      currentRoute: {},
      storefront: process.env.STOREFRONT,
      showPaybrightModal: false,
      showAffirmModal: false,
      payBrightMoreInfoUrl: null,
      affirmModalUrl: null,
      promotion: {},
      roomIdeasTracking: {},
      // showPromotionalFinancingPage: false,
      promoFinPage: {},
      currentPromo: '',
      customerToken: '',
    };

    if (initialState && initialState.common) {
      const common = initialState && initialState.common ? initialState.common : {};

      state = Object.assign(state, common);

      let hasClearance = common.clearanceNavigationEnabled; // backward compatible
      if (typeof hasClearance !== 'boolean') {
        hasClearance = LocationService.isCanada() ? common.hasClearanceProductsCA : common.hasClearanceProductsUS;
      }
      state = Object.assign(state, {
        menuItems: formatMenuItems(common.categories, common.primaryRooms, {
          showGiftCards: common.giftCardNavigationEnabled,
          // showPromotion: common.promotion,
          promotion: common.promotion,
          showClearance: hasClearance,
        }),
        showContactBanner: getContactBannerABTestCondition(),
        showMobileCategoryMenu: getMobileCategoryMenuTestCondition(),
        searchQuery: UrlUtils.getQueryParameter('q'),
        enableSearchSuggestions: getNewSearchABTestCondition(),
        cartCount: getCartQuantity(),
        cart: UserService.getCart(),
        financing: common.financing,
        payment: common.payment,
        primaryRooms: common.primaryRooms,
        promoFinPage: common.promotion ? getFinPromoPage(common) : {},
        currentPromo: common.promotion ? getCurrentPromo(common.promotion) : '',
      });
      state.clearanceNavigationEnabled = hasClearance;
    }

    if (initialState && initialState.breadcrumb) {
      const { setBreadcrumb } = useBreadcrumb();
      setBreadcrumb(initialState.breadcrumb);
    }

    state.countryId = LocationService.getCountryId();
    state.currencyId = LocationService.getCurrencyId();

    return {
      namespaced: true,
      state,
      mutations,
      getters,
      actions,
    };
  }
}

function getNewSearchABTestCondition() {
  // Search toggle. true for enabling Algolia search suggestions, false for disabling search suggestions
  return true;
}

function getContactBannerABTestCondition() {
  // Remove all code related to this test after Black Friday 2019
  return true;
}

function getMobileCategoryMenuTestCondition() {
  return true;
}

function getBrowseRoomPageTestCondition() {
  return browseRoomPageTestCondition();
}

function formatMenuItems(initialCategories, initialRooms, { promotion, showClearance, showGiftCards }) {
  if (!initialCategories) {
    return [];
  }

  const groupedCategories = groupBy(initialCategories, 'parentId');
  const categories = initialCategories.filter((category) => !category.parentId);
  categories.forEach((category) => {
    const allCategory = {
      ...category,
      name: category.displayName || category.name,
      sort: 0,
    };
    const subCategories = [allCategory, ...(groupedCategories[category.id] || [])];
    category.items = subCategories.length > 1 ? subCategories : [];
  });

  const newCategories = remove(categories, (category) => category.name === 'NEW');
  const roomCategories = remove(categories, (category) => category.name.toLowerCase() === 'rooms');
  roomCategories.forEach((category) => category.items.sort((a, b) => a.sort - b.sort));

  categories.sort((a, b) => a.sort - b.sort);
  categories.forEach((category) => category.items.sort((a, b) => a.sort - b.sort));

  if (roomCategories?.length) {
    const roomCategory = roomCategories[0];
    roomCategory.url = '';
    roomCategory.items = roomCategory.items.filter((category) => category.name.toLowerCase() !== 'rooms');
    categories.push(roomCategory);
  }

  if (showGiftCards) {
    categories.push({
      id: 'giftcards',
      name: 'Gift Cards',
      url: router.resolve({ name: 'giftCardBuy' }).href,
    });
  }

  if (!!promotion || showClearance) {
    const showPromoFinPage =
      LocationService.isUS() && (promotion?.name.toLowerCase().includes('black friday') || promotion?.name.toLowerCase().includes('cyber monday'));

    const saleMenu = {
      id: 'sale',
      name: 'Sale',
      url: router.resolve({ name: 'sale' }).href,
    };

    if (promotion) {
      if (!showClearance && !showPromoFinPage) {
        saleMenu.url = router.resolve({ name: 'currentPromotion' }).href;
      } else {
        saleMenu.items = showClearance
          ? [
              {
                id: 'sale',
                parentId: 'sale',
                name: 'All Sale',
                url: router.resolve({ name: 'sale' }).href,
              },
            ]
          : [];

        saleMenu.items.push({
          id: 'promotion',
          parentId: 'sale',
          name: (LocationService.isCanada() && promotion.caName) || promotion.name,
          url: router.resolve({ name: 'currentPromotion' }).href,
        });

        if (showClearance) {
          saleMenu.items.push({
            id: 'clearance',
            parentId: 'sale',
            name: 'Clearance',
            url: router.resolve({ name: 'clearance' }).href,
          });
        }

        if (showPromoFinPage) {
          saleMenu.items.push({
            id: 'financing',
            parentId: 'sale',
            name: '0% APR Financing',
            url: router.resolve({ name: 'financing' }).href,
          });
        }
      }
    }

    categories.push(saleMenu);
  }

  if (newCategories && newCategories.length) {
    const newCategory = newCategories[0];
    newCategory.items = [];
    categories.push(newCategory);
  }

  return categories;
}

function getFinPromoPage(common) {
  return {
    bfFinPageTitle: common?.bfFinPageTitle,
    bfFinPageSubtitle: common?.bfFinPageSubtitle,
    bfFinPageImage: common?.bfFinPageImage,
    cmFinPageTitle: common?.cmFinPageTitle,
    cmFinPageSubtitle: common?.cmFinPageSubtitle,
    cmFinPageImage: common?.cmFinPageImage,
  };
}

function getCurrentPromo(promo) {
  if (promo?.name?.toLowerCase().includes('black friday')) return 'bf';
  if (promo?.name?.toLowerCase().includes('cyber monday')) return 'cm';
  return '';
}

async function loadCartData() {
  let result;
  // Same as fetchDataMiniCart
  if (process.env.STOREFRONT) {
    result = await executeQuery(LOAD_CART_QUERY, null, {
      fetchPolicy: 'network-only',
    });
    return result.storeMiniCartGet ?? {};
  }

  result = http.post('/api/cart/fetchdata', {
    checkoutInfo: {
      miniCart: true,
      split: false,
    },
  });

  return result;
}
